import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { SEO } from "@/components/SEO";
import { CompleteYourBuild } from "@/components/CompleteYourBuild";
import { trackMetaEvent } from "@/components/Analytics";
import { useCart } from "@/context/CartContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 2000;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState({ status: "checking", data: null, error: null });
  const attemptsRef = useRef(0);
  const purchaseTrackedRef = useRef(false);
  const { clearCart } = useCart();

  useEffect(() => {
    if (state.status === "paid" && !purchaseTrackedRef.current && sessionId) {
      purchaseTrackedRef.current = true;
      // Only a cart-based checkout (multiple `items`) touches the persistent
      // cart — a single-item Buy Now never wrote to it, so leave the cart
      // untouched in that case (customer may still have unrelated items
      // queued up for later).
      if (state.data?.items) clearCart();
      trackMetaEvent("Purchase", {
        content_ids: state.data?.items ? state.data.items.map((i) => i.product_path) : [state.data?.product_path],
        content_name: state.data?.product_name,
        content_type: "product",
        currency: (state.data?.currency || "nzd").toUpperCase(),
        value: (state.data?.amount_total || 0) / 100,
        event_id: sessionId,
      });
    }
  }, [state.status, state.data, sessionId, clearCart]);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null, error: "Missing checkout session." });
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState({ status: "timeout", data: null, error: null });
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, error: null });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data, error: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setState({ status: "error", data: null, error: err?.response?.data?.detail || "Could not verify payment status." });
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page" className="min-h-[70vh] flex items-center justify-center container-x py-24">
      <SEO path="/checkout/success" title="Order Status · XpeditioNZ" description="Order confirmation status." />
      <div className={`w-full text-center ${state.status === "paid" ? "max-w-3xl" : "max-w-lg"}`}>
        {(state.status === "checking") && (
          <div data-testid="checkout-status-checking">
            <Loader2 size={40} className="animate-spin text-[var(--brand-primary)] mx-auto mb-6" />
            <h1 className="font-display text-2xl md:text-3xl text-white mb-3">Confirming Your Payment…</h1>
            <p className="text-[var(--text-secondary)]">Please don't close this page.</p>
          </div>
        )}

        {state.status === "paid" && (
          <div data-testid="checkout-status-paid">
            <CheckCircle2 size={48} className="text-emerald-400 mx-auto mb-6" />
            <h1 className="font-display text-3xl md:text-4xl text-white mb-3">ORDER CONFIRMED.</h1>
            {state.data?.items && state.data.items.length > 1 ? (
              <div data-testid="checkout-order-items" className="text-left max-w-md mx-auto mb-6 border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
                {state.data.items.map((item, i) => (
                  <div key={item.product_name || i} className="flex justify-between px-4 py-3 text-sm text-[var(--text-secondary)]">
                    <span>{item.product_name} x{item.quantity}</span>
                    <span className="text-white font-mono">${((item.unit_amount_cents * item.quantity) / 100).toLocaleString("en-NZ")}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[var(--text-secondary)] leading-relaxed mb-2">
                Thanks for your order — {state.data?.product_name} x{state.data?.quantity}.
              </p>
            )}
            <p className="text-[var(--text-secondary)] leading-relaxed mb-8">
              A confirmation email is on its way. We'll be in touch shortly with delivery/collection details.
            </p>
            <Link to="/" data-testid="checkout-back-home" className="btn-primary">Back To Home</Link>
            <CompleteYourBuild productPath={state.data?.items ? state.data.items[0].product_path : state.data?.product_path} />
          </div>
        )}

        {(state.status === "timeout" || state.status === "error" || state.status === "expired") && (
          <div data-testid="checkout-status-issue">
            <AlertCircle size={44} className="text-red-400 mx-auto mb-6" />
            <h1 className="font-display text-2xl md:text-3xl text-white mb-3">
              {state.status === "expired" ? "Session Expired" : "Still Confirming…"}
            </h1>
            <p className="text-[var(--text-secondary)] leading-relaxed mb-8">
              {state.error || "We couldn't confirm your payment status yet. If you completed payment, check your email for confirmation or contact us directly."}
            </p>
            <Link to="/" data-testid="checkout-back-home" className="btn-secondary">Back To Home</Link>
          </div>
        )}
      </div>
    </div>
  );
}
